import React, { useEffect, useMemo, useState } from 'react';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';
import SimpleUserCard from '../../components/SimpleUserCard';
import { useNavigate } from 'react-router-dom';
import {
  assessmentStatus,
  defaultAssessmentTypes,
  otherTemplateTypes,
  paths,
} from '../../constants/strings';
import { Icon } from '@mui/material';
import { useWorkspace } from '../../providers/WorkspaceProvider.jsx';
import { getRoute } from '../../constants/strings';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys.js';
import { getColor } from '../../constants/strings';
import {
  privateColumn,
  useTableComponents,
  AutocompleteFilter,
} from '../../components/tables/MTableComponents.jsx';
import MTTable from '../../components/tables/MTable.jsx';
import { useURLParams } from '../../providers/URLParamProvider.jsx';
import { CustomDateRangeFilter } from '../../components/tables/MTableComponents.jsx';
import { useQueryParams } from '../../hooks/misc.js';
import { useLabels } from '../../providers/LabelProvider.jsx';
import { LabelFilter } from '../../components/tables/MTableComponents.jsx';
import { useCurrentUser } from '../../providers/UserProvider.jsx';
import { dateTypes, getDateRange } from '../../components/tables/table.js';
import { deepCompareObjects } from '../../utilities/misc.js';
import { useFlags } from 'launchdarkly-react-client-sdk';
import MultiAvatar from '../../components/MultiAvatar.jsx';
import Text from '../../components/typography/Text.jsx';
import useCheckPermission from '../../providers/UsePermissionHook.jsx';
import { actions } from '../../utilities/misc.js';

const inspectionsQuery = loader('./InspectionsList.graphql');

export default function InspectionsList() {
  const {
    workspaceVehicles,
    availableWorkspaces,
    workspaceId: providerWorkspace,
  } = useWorkspace();
  const { labels } = useLabels();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    filters,
    page,
    pageSize,
    sort,
    subfilters,
    cachedDateFilters,
    segment,
  } = useURLParams();
  const { isAdmin, allTemplateTypes = {} } = useCurrentUser();
  const { getParam } = useQueryParams();
  const { disableAssessmentSubject } = useFlags();
  const tab = getParam('tab');
  // Quality module only: COMPANY_WIDE|TYPE shows this type's assessments from
  // every workspace
  const hasCompanyWideAccess =
    useCheckPermission({
      model: 'inspectionTemplate',
      action: actions.companyWide,
      type: tab?.toUpperCase() || 'QUALITY',
    }) && segment === 'quality';

  const [dateSelectors, setDateSelectors] = useState(() => {
    return dateTypes.reduce((acc, [field, minKey, maxKey]) => {
      const [min, max] = getDateRange(
        cachedDateFilters?.[field],
        getParam(field),
      );
      acc[minKey] = min;
      acc[maxKey] = max;
      return acc;
    }, {});
  });

  const inspectionType = getParam('inspectionType') || getParam('lockedType');

  const workspaces = availableWorkspaces?.map((w) => ({
    label: `${w.title}`,
    value: `${w.id}`,
  }));
  workspaces.push({ label: `${t(keys.common.NONE)}`, value: 'null' });

  const {
    multiLabelCell,
    labelCell,
    dateCell,
    assetCell,
    assetFilterOptions,
    workspaceCell,
    textCell,
    workspaceFilterOptions,
    multiAvatarCell,
    userFilterOptions,
  } = useTableComponents();

  const allWorkspaces = getParam('allWorkspaces') === 'TRUE';

  const allFilters = useMemo(() => {
    let operator = 'not';
    let value = ['INCIDENT', 'AUDIT', 'OBSERVATION', 'QUALITY', 'MOC'];
    if (segment === 'quality') {
      value = tab?.toUpperCase() || 'QUALITY';
      operator = 'eq';
    } else if (!!inspectionType) {
      operator = 'eq';
      value = `${inspectionType}`;
    }
    return [
      ...filters,
      {
        field: 'inspectionType',
        operator,
        value,
      },
    ];
  }, [filters, inspectionType, segment, tab]);

  const labelsParam =
    getParam('labels')
      ?.split(',')
      .map((n) => labels.find(({ id }) => id === parseInt(n))) || [];
  const [selectedLabels, setSelectedLabels] = useState(labelsParam);
  useEffect(() => {
    if (!deepCompareObjects(labelsParam, selectedLabels)) {
      setSelectedLabels(labelsParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelsParam]);

  const {
    refetch,
    networkStatus,
    data: { inspections = [], inspectionsCount = 0 } = {},
    previousData: { inspectionsCount: oldCount } = {},
  } = useQuery(inspectionsQuery, {
    skip: !page || !sort || !pageSize,
    variables: {
      options: {
        page,
        pageSize,
        sort,
        filters: [...allFilters],
        subfilters,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const data = useMemo(() => inspections, [inspections]);

  const filterableTypes = Object.values(allTemplateTypes)
    .filter((f) => !otherTemplateTypes.includes(f.key))
    .map((a) => ({
      label: a.name === 'Fleet' ? 'Asset' : a.name || a.title,
      value: a.key,
    }));

  const columns = useMemo(
    () =>
      [
        { ...privateColumn },
        {
          accessorKey: 'inspectionType',
          header: segment === 'quality' ? '' : t(keys.common.TYPE),
          size: segment === 'quality' ? 75 : 100,
          filterVariant: 'select',
          enableColumnFilter: segment !== 'quality',
          filterSelectOptions: filterableTypes,
          Cell: ({ cell }) => {
            const type = cell.getValue()?.toLowerCase() || '';
            const color = getColor(type, allTemplateTypes);
            const icon =
              type === 'vehicle'
                ? defaultAssessmentTypes.fleet.icon
                : allTemplateTypes[type]?.icon ||
                  defaultAssessmentTypes[type]?.icon;
            return (
              <Icon
                baseClassName="material-icons-outlined"
                sx={{
                  fontSize: '1.25rem',
                  color: color,
                }}
              >
                {icon || 'star_border'}
              </Icon>
            );
          },
        },
        {
          id: 'templateTitle',
          accessorFn: (row) => row?.template?.title ?? '',
          header: t(keys.common.TITLE),
          grow: true,
          enableSorting: false,
          minSize: 250,
          Cell: ({ cell }) => textCell({ cell }),
        },
        ...(!disableAssessmentSubject
          ? [
              {
                accessorKey: 'subject',
                header: 'Subject',
                minSize: 250,
                Cell: ({ cell }) => {
                  return textCell({ cell });
                },
              },
            ]
          : []),
        {
          accessorKey: 'location',
          header: t(keys.common.LOCATION),
          Cell: ({ cell }) => textCell({ cell }),
        },
        {
          accessorKey: 'creator',
          header: t(keys.common.CREATOR),
          grow: true,
          enableSorting: false,
          Filter: ({ column }) => (
            <AutocompleteFilter
              items={userFilterOptions(
                isAdmin || !!allWorkspaces ? 'all' : 'workspace',
              )}
              column={column}
              type="user"
              filterBy={t(keys.common.CREATOR)}
            />
          ),
          Cell: ({ cell }) => (
            <SimpleUserCard
              weight="semiBold"
              size="sm"
              user={cell.getValue()}
            />
          ),
        },
        {
          accessorKey: 'participants',
          header: t(keys.common.PARTICIPANTS),
          enableSorting: false,
          Cell: ({ row, cell }) =>
            row.original?.creator?.id ? (
              multiAvatarCell({ cell })
            ) : (
              <Text noMargin size="sm" color="secondary">
                {t(keys.common.NONE)}
              </Text>
            ),
          Filter: ({ column }) => (
            <AutocompleteFilter
              items={userFilterOptions(
                isAdmin || !!allWorkspaces ? 'all' : 'workspace',
              )}
              column={column}
              filterBy={t(keys.common.PARTICIPANTS)}
              type="user"
            />
          ),
        },
        {
          accessorKey: 'status',
          header: t(keys.common.STATUS),
          Header: ({ column }) => (
            <div style={{ textWrap: 'nowrap' }}>{column.columnDef.header}</div>
          ),
          size: 150,
          grow: true,
          filterVariant: 'select',
          filterSelectOptions: [
            { value: 'IN_PROGRESS', label: t(keys.common.IN_PROGRESS) },
            { value: 'COMPLETE', label: t(keys.common.COMPLETE) },
            { value: 'PENDING_ACTION', label: t(keys.common.PENDING_ACTION) },
            { value: 'REVIEW', label: t(keys.assessments.PENDING_REVIEW) },
            {
              value: 'CHANGE_REQUEST',
              label: t(keys.assessments.CHANGE_REQUEST_NO_VAR),
            },
          ],
          Cell: ({ cell }) => {
            const statusKey = cell.getValue();
            const meta = assessmentStatus?.[statusKey];
            return labelCell({
              name: meta ? t(meta.title) : t(keys.common.UNKNOWN),
              color: meta?.color ?? '#9e9e9e',
            });
          },
        },
        {
          accessorKey: 'passed',
          header: t(keys.common.PASSED),
          size: 100,
          grow: true,
          filterVariant: 'select',
          filterSelectOptions: [
            { value: 'true', label: t(keys.common.PASSED) },
            { value: 'false', label: t(keys.common.FAILED) },
          ],
          Cell: ({ cell }) => (
            <Icon
              baseClassName="material-icons-outlined"
              sx={{
                color: cell.getValue() ? '#28a745' : '#e9222c',
                fontSize: '1.25rem',
              }}
            >
              {cell.getValue() ? 'check' : 'close'}
            </Icon>
          ),
        },
        {
          accessorKey: 'documentNumber',
          header: t(keys.assessments.DOCUMENT_NUMBER),
          grow: true,
          enableSorting: false,
          Cell: ({ cell }) => textCell({ cell }),
        },
        {
          accessorKey: 'vehicle',
          header: t(keys.common.ASSET),
          grow: true,
          enableSorting: false,
          Cell: ({ cell }) => assetCell({ cell }),
          Filter: ({ column }) => (
            <AutocompleteFilter
              items={assetFilterOptions(workspaceVehicles)}
              type="vehicle"
              filterBy={t(keys.common.ASSET)}
              column={column}
            />
          ),
        },
        {
          accessorKey: 'workspace',
          header: t(keys.common.WORKSPACE),
          enableSorting: false,
          grow: true,
          Cell: ({ cell }) => workspaceCell({ cell }),
          Filter: ({ column }) => (
            <AutocompleteFilter
              items={workspaceFilterOptions({
                includePublic: true,
                hasCompanyWideAccess,
              })}
              type="workspace"
              filterBy={t(keys.common.WORKSPACE)}
              column={column}
            />
          ),
        },
        {
          accessorKey: 'attachments',
          header: `${t(keys.common.SIGNATURE)}s`,
          enableSorting: false,
          enableColumnFilter: false,
          grow: true,
          Cell: ({ cell }) => {
            const signatures = cell.getValue() || [];
            const users = signatures
              ?.filter((s) => s.extension === 'SIGNATURE')
              ?.map((u) => u.creator);
            return (
              <div style={{ marginLeft: '0.4rem' }}>
                <MultiAvatar users={users} maxLength={5} size="xs" />
              </div>
            );
          },
        },
        {
          accessorKey: 'dateModified',
          header: t(keys.common.LAST_UPDATED),
          size: 275,
          Cell: ({ cell }) => dateCell({ cell }),
          Filter: ({ column }) => (
            <CustomDateRangeFilter
              column={column}
              minDate={dateSelectors.dateModifiedMin}
              maxDate={dateSelectors.dateModifiedMax}
              setMinDate={(value) =>
                setDateSelectors({ ...dateSelectors, dateModifiedMin: value })
              }
              setMaxDate={(value) =>
                setDateSelectors({ ...dateSelectors, dateModifiedMax: value })
              }
            />
          ),
        },
        {
          accessorKey: 'dateCreated',
          header: t(keys.common.DATE_CREATED),
          size: 275,
          Cell: ({ cell }) => dateCell({ cell }),
          Filter: ({ column }) => (
            <CustomDateRangeFilter
              column={column}
              minDate={dateSelectors.dateCreatedMin}
              maxDate={dateSelectors.dateCreatedMax}
              setMinDate={(value) =>
                setDateSelectors({ ...dateSelectors, dateCreatedMin: value })
              }
              setMaxDate={(value) =>
                setDateSelectors({ ...dateSelectors, dateCreatedMax: value })
              }
            />
          ),
        },
        {
          accessorKey: 'dateCompleted',
          header: t(keys.common.DATE_COMPLETED),
          size: 275,
          Cell: ({ cell }) => dateCell({ cell }),
          Filter: ({ column }) => (
            <CustomDateRangeFilter
              column={column}
              minDate={dateSelectors.dateCompletedMin}
              maxDate={dateSelectors.dateCompletedMax}
              setMinDate={(value) =>
                setDateSelectors({ ...dateSelectors, dateCompletedMin: value })
              }
              setMaxDate={(value) =>
                setDateSelectors({ ...dateSelectors, dateCompletedMax: value })
              }
            />
          ),
        },
        {
          accessorKey: 'labels',
          header: t(keys.common.LABELS),
          enableSorting: false,
          size: 300,
          Filter: ({ column }) => (
            <LabelFilter
              column={column}
              selectedLabels={selectedLabels}
              labels={labels}
              handleChange={(label) => {
                setSelectedLabels(label);
              }}
              handleRemove={() => {
                column.setFilterValue(null);
                setSelectedLabels([]);
              }}
            />
          ),
          Cell: ({ cell }) => {
            const labels = cell.getValue() || [];
            return multiLabelCell({ labels });
          },
        },
        {
          accessorKey: 'incidents',
          header: `${t(keys.assessments.RELATED_INCIDENTS)}`,
          enableSorting: false,
          enableColumnFilter: false,
          grow: true,
          Cell: ({ row }) => {
            const hasIncidents = (row?.original?.incidents?.length || 0) > 0;
            return (
              <Icon
                baseClassName="material-icons-outlined"
                sx={{
                  color: hasIncidents ? '#28a745' : '#e9222c',
                  fontSize: '1.25rem',
                }}
              >
                {hasIncidents ? 'check' : 'close'}
              </Icon>
            );
          },
        },
      ].filter(Boolean),
    [
      t,
      segment,
      filterableTypes,
      disableAssessmentSubject,
      allTemplateTypes,
      textCell,
      userFilterOptions,
      isAdmin,
      allWorkspaces,
      multiAvatarCell,
      labelCell,
      assetCell,
      assetFilterOptions,
      workspaceVehicles,
      workspaceCell,
      workspaceFilterOptions,
      hasCompanyWideAccess,
      dateCell,
      dateSelectors,
      selectedLabels,
      labels,
      multiLabelCell,
    ],
  );
  const loading = networkStatus === networkStatus.loading;

  return (
    <MTTable
      refetch={refetch}
      isLoading={loading}
      rightClickNavigation={paths.assessment}
      columns={columns}
      data={data || []}
      rowCount={inspectionsCount || oldCount}
      onRowClick={(assessment, row) => {
        // Company-wide viewers open records from workspaces they aren't in
        // under their current workspace, since those routes aren't valid
        const inUserWorkspace = availableWorkspaces?.some(
          ({ id }) => id === assessment?.workspaceId,
        );
        const workspaceId =
          (inUserWorkspace && assessment?.workspaceId) || providerWorkspace;
        const listIds = data?.map(({ id }) => id);
        navigate(
          getRoute(
            workspaceId,
            paths.assessment,
            assessment.id,
            `?type=${assessment?.inspectionType}`,
          ),
          {
            state: {
              listIds,
              index: row.index,
            },
          },
        );
      }}
      onClearAllFilters={() => {
        setSelectedLabels([]);
        setDateSelectors({});
      }}
      enableRowVirtualization
      mrtVirtualizerOptions={{ overscan: 5 }}
      showAllWorkspacesCheck
      listName="assessments"
    />
  );
}
